import { FC, useCallback, useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useDidMount } from "rooks";
import { languageOptions } from "@/components/common/language-select/utils/constants/constants";
import { AvailableLanguages } from "@/components/common/language-select/utils/types/types";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { languageSelector } from "@/store/language-select/selectors/languageSelector";
import { languageLoadingSelector } from "@/store/language-select/selectors/languageLoadingSelector";
import { saveLanguageThunk } from "@/store/language-select/thunks/saveLanguageThunk";
import { getLanguageThunk } from "@/store/language-select/thunks/getLanguageThunk";

export const LanguageSelect: FC = () => {
  const dispatch = useAppDispatch();
  const languageFromPreferences = useAppSelector(languageSelector);
  const loading = useAppSelector(languageLoadingSelector);

  const [language, setLanguage] = useState<AvailableLanguages>(
    languageFromPreferences ?? "en",
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<AvailableLanguages>) => {
      setLanguage(event.target.value as AvailableLanguages);
      dispatch(saveLanguageThunk(event.target.value as AvailableLanguages));
    },
    [dispatch],
  );

  useEffect(() => {
    setLanguage(languageFromPreferences ?? "en");
  }, [languageFromPreferences]);

  useDidMount(() => {
    dispatch(getLanguageThunk());
  });

  return (
    <FormControl size="small" fullWidth sx={formControlStyles}>
      <InputLabel>Language</InputLabel>

      <Select
        size="small"
        label="Language"
        disabled={loading}
        value={language}
        sx={{ height: 32 }}
        onChange={handleChange}
      >
        {languageOptions.map(({ title, code }) => (
          <MenuItem key={code} value={code}>
            <ListItemText>
              <Typography variant="caption" color="text.primary">
                {title}
              </Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const formControlStyles = { minWidth: 120, height: 32 };
